import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import {
  Dog, Search, RotateCcw, ChevronLeft, ChevronRight,
  Calendar, MapPin, Download, Eye,
  Loader2, Syringe, ShieldCheck, AlertTriangle, ClipboardList,
  X, User, Phone, CreditCard, Home,
  LayoutGrid, List, ArrowUpDown
} from 'lucide-react';
import * as XLSX from 'xlsx';
import usePetRegistrations, { VACCINATION_STATUS } from '../hooks/usePetRegistrations';
import useUnionCouncils from '../hooks/useUnionCouncils';
import { useAuth } from '../context/AuthContext';
import { DISTRICTS, getTehsilsForDistrict, getDistrictsForDivision } from '../constants';

const PetRegisteredView = () => {
  const { darkMode } = useOutletContext();
  const location = useLocation();
  const { adminScope } = useAuth();

  // Scope-aware options
  const isDistrictLocked = adminScope?.accessLevel === 'district' || adminScope?.accessLevel === 'tehsil';
  const isTehsilLocked = adminScope?.accessLevel === 'tehsil';

  // Scope-aware districts
  const scopeDistricts = useMemo(() => {
    if (!adminScope) return DISTRICTS;
    if (adminScope.accessLevel === 'tehsil' && adminScope.district) return [adminScope.district];
    if (adminScope.accessLevel === 'district' && adminScope.district) return [adminScope.district];
    if (adminScope.accessLevel === 'division' && adminScope.division) return getDistrictsForDivision(adminScope.division);
    return DISTRICTS; // province
  }, [adminScope]);

  // Filter states
  const [filters, setFilters] = useState({
    district: adminScope?.district || '',
    tehsil: adminScope?.tehsil || '',
    uc: '',
    status: '',
    startDate: '',
    endDate: '',
    searchQuery: ''
  });

  // Available tehsils based on selected district
  const scopeTehsils = useMemo(() => {
    if (isTehsilLocked && adminScope?.tehsil) return [adminScope.tehsil];
    if (filters.district) return getTehsilsForDistrict(filters.district);
    return scopeDistricts.flatMap(d => getTehsilsForDistrict(d));
  }, [filters.district, adminScope, isTehsilLocked, scopeDistricts]);

  // Use the pet registrations hook
  const { data: pets, loading, error, refetch } = usePetRegistrations({
    district: filters.district,
    tahsil: filters.tehsil,
    uc: filters.uc,
    status: filters.status,
    startDate: filters.startDate,
    endDate: filters.endDate
  });

  // Union councils for filter (scope-aware)
  const { data: unionCouncilsList } = useUnionCouncils({ district: filters.district, tahsil: filters.tahsil });

  // Filter UCs based on selected Tehsil
  const availableUCs = useMemo(() => {
    let ucs = unionCouncilsList || [];
    if (filters.tehsil) {
      ucs = ucs.filter(uc => uc.tahsil === filters.tehsil);
    }
    // Sort by UC Number
    return [...ucs].sort((a, b) => {
      const numA = parseInt((a.uc || a.name || '').replace(/\D/g, '')) || 0;
      const numB = parseInt((b.uc || b.name || '').replace(/\D/g, '')) || 0;
      return numA - numB;
    });
  }, [unionCouncilsList, filters.tehsil]);

  const [refreshing, setRefreshing] = useState(false);

  // View mode and sorting
  const [viewMode, setViewMode] = useState('grid'); // 'list' or 'grid'
  const [sortBy, setSortBy] = useState('date_desc'); // sorting option

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setTimeout(() => setRefreshing(false), 500);
  };

  // Local search filtering and sorting
  const filteredPets = useMemo(() => {
    let result = pets;

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(pet =>
        pet.petName?.toLowerCase().includes(query) ||
        pet.ownerName?.toLowerCase().includes(query) ||
        pet.ownerPhone?.toLowerCase().includes(query) ||
        pet.uc?.toLowerCase().includes(query) ||
        pet.breed?.toLowerCase().includes(query) ||
        pet.registrationId?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return (b.date || 0) - (a.date || 0);
        case 'date_asc':
          return (a.date || 0) - (b.date || 0);
        case 'status':
          return (a.vaccinationStatus || '').localeCompare(b.vaccinationStatus || '');
        case 'tehsil':
          return (a.tahsil || '').localeCompare(b.tahsil || '');
        case 'name':
          return (a.petName || '').localeCompare(b.petName || '');
        case 'owner':
          return (a.ownerName || '').localeCompare(b.ownerName || '');
        case 'uc':
          // Extract number from UC name (e.g., "UC-001" -> 1, "UC 12" -> 12)
          const getUCNumber = (record) => {
            const ucName = record.uc || '';
            const match = ucName.match(/UC[-\s]?(\d+)/i);
            return match ? parseInt(match[1], 10) : 999;
          };
          return getUCNumber(a) - getUCNumber(b);
        default:
          return 0;
      }
    });

    return result;
  }, [pets, filters.searchQuery, sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Auto-open modal if pet ID is passed from navigation (e.g., from map view)
  useEffect(() => {
    if (location.state?.selectedPetId && pets.length > 0) {
      const petToOpen = pets.find(
        pet => pet.docId === location.state.selectedPetId || pet.id === location.state.selectedPetId
      );
      if (petToOpen) {
        setSelectedPet(petToOpen);
        setIsModalOpen(true);
        // Clear the location state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state?.selectedPetId, pets]);

  // Calculate stats based on vaccination status
  const stats = useMemo(() => {
    const total = filteredPets.length;
    const registered = filteredPets.filter(pet => pet.vaccinationStatus === VACCINATION_STATUS.REGISTERED).length;
    const firstShot = filteredPets.filter(pet => pet.vaccinationStatus === VACCINATION_STATUS.FIRST_SHOT).length;
    const firstExpired = filteredPets.filter(pet => pet.vaccinationStatus === VACCINATION_STATUS.FIRST_EXPIRED).length;
    const vaccinated = filteredPets.filter(pet => pet.vaccinationStatus === VACCINATION_STATUS.VACCINATED).length;
    const vaccinationExpired = filteredPets.filter(pet => pet.vaccinationStatus === VACCINATION_STATUS.VACCINATION_EXPIRED).length;

    return { total, registered, firstShot, firstExpired, vaccinated, vaccinationExpired };
  }, [filteredPets]);

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      district: isDistrictLocked ? (adminScope?.district || '') : '',
      tehsil: isTehsilLocked ? (adminScope?.tehsil || '') : '',
      uc: '',
      status: '',
      startDate: '',
      endDate: '',
      searchQuery: ''
    });
    setSortBy('date_desc');
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPets = filteredPets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPets.length / itemsPerPage);

  // Export to CSV
  const handleExportCSV = () => {
    const csvData = filteredPets.map(pet => ({
      'Date & Time': pet.date ? pet.date.toLocaleString() : 'N/A',
      'Pet Name': pet.petName,
      'Breed': pet.breed,
      'Age': pet.age,
      'Body Color': pet.bodyCoatColour,
      'Owner Name': pet.ownerName,
      'Owner Phone': pet.ownerPhone,
      'Owner CNIC': pet.ownerCNIC,
      'Owner Address': pet.ownerAddress,
      'UC': pet.uc,
      'Tehsil': pet.tahsil,
      'District': pet.district,
      'Status': pet.status,
      'Vaccination Status': pet.vaccinationStatus,
      'Hospital Type': pet.hospitalType
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pet Registrations');
    XLSX.writeFile(wb, `pet_registrations_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Get status badge styling
  const getStatusBadge = (vaccinationStatus) => {
    switch (vaccinationStatus) {
      case VACCINATION_STATUS.VACCINATED:
        return {
          bg: darkMode ? 'bg-emerald-900/50' : 'bg-emerald-100',
          text: darkMode ? 'text-emerald-300' : 'text-emerald-700',
          label: 'Vaccinated'
        };
      case VACCINATION_STATUS.FIRST_SHOT:
        return {
          bg: darkMode ? 'bg-cyan-900/50' : 'bg-cyan-100',
          text: darkMode ? 'text-cyan-300' : 'text-cyan-700',
          label: 'First Shot'
        };
      case VACCINATION_STATUS.FIRST_EXPIRED:
        return {
          bg: darkMode ? 'bg-red-900/50' : 'bg-red-100',
          text: darkMode ? 'text-red-300' : 'text-red-700',
          label: 'First Shot Expired'
        };
      case VACCINATION_STATUS.VACCINATION_EXPIRED:
        return {
          bg: darkMode ? 'bg-red-900/50' : 'bg-red-100',
          text: darkMode ? 'text-red-300' : 'text-red-700',
          label: 'Vaccination Expired'
        };
      default:
        return {
          bg: darkMode ? 'bg-purple-900/50' : 'bg-purple-100',
          text: darkMode ? 'text-purple-300' : 'text-purple-700',
          label: 'Registered'
        };
    }
  };

  const handleModelOpen = (pet) => {
    setSelectedPet(pet);
    setIsModalOpen(true);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Pet Registered View
            </h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Detailed records of registered pets and vaccination status
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              disabled={filteredPets.length === 0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all bg-emerald-600 hover:bg-emerald-700 text-white ${filteredPets.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Download size={18} />
              Export CSV
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className={`p-2.5 rounded-lg transition-all ${darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                } ${(refreshing || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Refresh data"
            >
              <RotateCcw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Stats Cards — clickable to filter by status */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">

          {/* Total */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: '' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 ${filters.status === ''
              ? 'ring-blue-500'
              : 'ring-transparent hover:ring-blue-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <ClipboardList size={20} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
              </div>
            </div>
          </button>

          {/* Registered */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'Registered' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 ${filters.status === 'Registered'
              ? 'ring-gray-500'
              : 'ring-transparent hover:ring-gray-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <Dog size={20} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Registered <span className="text-[10px] opacity-60">(Not Vaccinated)</span></p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{stats.registered}</p>
              </div>
            </div>
          </button>

          {/* First Shot */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'First Shot' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 ${filters.status === 'First Shot'
              ? 'ring-yellow-500'
              : 'ring-transparent hover:ring-yellow-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                <Syringe size={20} className={darkMode ? 'text-yellow-400' : 'text-yellow-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>First Shot</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.firstShot}</p>
              </div>
            </div>
          </button>

          {/* First Expired */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'First Shot Expired' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 ${filters.status === 'First Shot Expired'
              ? 'ring-orange-500'
              : 'ring-transparent hover:ring-orange-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-orange-900/50' : 'bg-orange-100'}`}>
                <AlertTriangle size={20} className={darkMode ? 'text-orange-400' : 'text-orange-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>First Shot Expired</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>{stats.firstExpired}</p>
              </div>
            </div>
          </button>

          {/* Vaccinated */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'Vaccinated' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 ${filters.status === 'Vaccinated'
              ? 'ring-green-500'
              : 'ring-transparent hover:ring-green-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                <ShieldCheck size={20} className={darkMode ? 'text-green-400' : 'text-green-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vaccinated</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.vaccinated}</p>
              </div>
            </div>
          </button>

          {/* Vaccination Expired */}
          <button
            onClick={() => setFilters(f => ({ ...f, status: 'Vaccination Expired' }))}
            className={`p-4 rounded-xl shadow-sm text-left transition-all duration-200 ring-2 col-span-2 sm:col-span-1 ${filters.status === 'Vaccination Expired'
              ? 'ring-red-500'
              : 'ring-transparent hover:ring-red-300'
              } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                <AlertTriangle size={20} className={darkMode ? 'text-red-400' : 'text-red-600'} />
              </div>
              <div>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vax Expired</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{stats.vaccinationExpired}</p>
              </div>
            </div>
          </button>

        </div>

        {/* Filters */}
        <div className={`p-4 rounded-xl shadow-sm mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            {/* District Filter */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                District
              </label>
              <select
                value={filters.district}
                onChange={(e) => { if (!isDistrictLocked) setFilters({ ...filters, district: e.target.value, tehsil: '', uc: '' }); }}
                disabled={isDistrictLocked}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${isDistrictLocked ? 'opacity-60 cursor-not-allowed' : ''} ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">All Districts</option>
                {scopeDistricts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* Tehsil Filter */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Tehsil
              </label>
              <select
                value={filters.tehsil}
                onChange={(e) => setFilters({ ...filters, tehsil: e.target.value, uc: '' })}
                disabled={isTehsilLocked}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${isTehsilLocked ? 'opacity-60 cursor-not-allowed' : ''} ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">All Tehsils</option>
                {scopeTehsils.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* UC Filter */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Union Council
              </label>
              <select
                value={filters.uc}
                onChange={(e) => setFilters({ ...filters, uc: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">All UCs</option>
                {availableUCs.map(uc => (
                  <option key={uc.uid || uc.id} value={uc.uc}>
                    {uc.uc}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">All Status</option>
                <option value="Registered">Registered (Not Vaccinated)</option>
                <option value="First Shot">First Shot</option>
                <option value="First Shot Expired">First Shot Expired</option>
                <option value="Vaccinated">Vaccinated</option>
                <option value="Vaccination Expired">Vaccination Expired</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>

            {/* End Date */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className={`w-full px-3 py-2.5 rounded-lg border text-sm ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Search
              </label>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={16} />
                <input
                  type="text"
                  placeholder="Search by pet name, ID, owner, phone..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                />
              </div>
            </div>

            {/* Reset Button, Sort and View Toggle */}
            <div className="flex items-end gap-2">
              <button
                onClick={handleResetFilters}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
              >
                <RotateCcw size={16} />
                Reset Filters
              </button>

              {/* Sort Dropdown */}
              <div className="relative h-[42px]">
                <ArrowUpDown size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`h-full pl-8 pr-2 rounded-lg border text-sm appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-200 border-gray-300'}`}
                  title="Sort by"
                >
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="name">Name</option>
                  <option value="owner">Owner</option>
                  <option value="uc">UC</option>
                  <option value="status">Status</option>
                  <option value="tehsil">Tehsil</option>
                </select>
              </div>

              {/* View Toggle Buttons */}
              <div className="flex rounded-lg overflow-hidden h-[42px]">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 transition-all ${viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  title="List View"
                >
                  <List size={15} />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-2 transition-all ${viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  title="Grid View"
                >
                  <LayoutGrid size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm ${darkMode ? 'text-red-200' : 'text-red-800'}`}>Error: {error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="animate-spin text-blue-600" size={40} />
            <span className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading pet records...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredPets.length === 0 && (
          <div className={`rounded-xl shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'} text-center p-12`}>
            <Dog className={`mx-auto h-12 w-12 mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No pet records found
            </p>
            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Try adjusting your filters
            </p>
          </div>
        )}

        {/* Grid View */}
        {!loading && filteredPets.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {currentPets.map((pet) => {
              const statusBadge = getStatusBadge(pet.vaccinationStatus);

              // Determine vaccination status text
              const getVaccinationStatusText = () => {
                switch (pet.vaccinationStatus) {
                  case VACCINATION_STATUS.VACCINATED:
                    return 'Fully Vaccinated';
                  case VACCINATION_STATUS.FIRST_SHOT:
                    return 'First Shot Given';
                  case VACCINATION_STATUS.FIRST_EXPIRED:
                    return 'First Shot Expired';
                  case VACCINATION_STATUS.VACCINATION_EXPIRED:
                    return 'Vaccination Expired';
                  default:
                    return 'Registered — Not Vaccinated';
                }
              };

              return (
                <div
                  key={pet.id}
                  className={`group ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300`}
                >
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {pet.imageUrl ? (
                      <img
                        src={pet.imageUrl}
                        alt={pet.petName}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <Dog size={48} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                      </div>
                    )}

                    {/* Status Badge - Top Right */}
                    <div className="absolute top-2 right-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium shadow ${statusBadge.bg} ${statusBadge.text}`}>
                        {statusBadge.label}
                      </div>
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="p-4">
                    {/* Pet Name */}
                    <h3 className={`text-base font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {pet.petName || 'Unknown'}
                    </h3>

                    {/* Registration ID */}
                    <p className={`text-sm font-mono font-semibold mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      {pet.registrationId || 'N/A'}
                    </p>

                    {/* Breed • Gender */}
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {pet.breed || 'N/A'} • {pet.sex || 'N/A'}
                    </p>

                    {/* Owner Info */}
                    <div className="flex items-center gap-2 mb-2">
                      <User size={14} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {pet.ownerName || 'N/A'}
                      </p>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin size={14} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {pet.tahsil || 'N/A'}
                      </p>
                    </div>

                    {/* Vaccination Status Indicator */}
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar size={14} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getVaccinationStatusText()}
                      </p>
                    </div>

                    {/* View Details Button */}
                    <button
                      onClick={() => handleModelOpen(pet)}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all border ${darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View (Table) */}
        {!loading && filteredPets.length > 0 && viewMode === 'list' && (
          <div className={`rounded-xl shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className={darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Date & Time
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Pet Info
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Owner
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Location
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Status
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {currentPets.map((pet) => {
                    const statusBadge = getStatusBadge(pet.vaccinationStatus);
                    return (
                      <tr key={pet.id} className={`${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'} transition-colors`}>
                        {/* Date & Time */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <Calendar size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {pet.date ? pet.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {pet.date ? pet.date.toLocaleTimeString() : ''}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Pet Info */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 h-10 w-10">
                              {pet.imageUrl ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={pet.imageUrl}
                                  alt={pet.petName}
                                />
                              ) : (
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                                  <Dog className={darkMode ? 'text-blue-400' : 'text-blue-600'} size={20} />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {pet.petName || 'Unknown'}
                              </p>
                              <p className={`text-xs font-mono font-semibold leading-none mt-1 mb-1.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                ID: {pet.registrationId || 'N/A'}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {pet.breed || 'N/A'} • {pet.age || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Owner */}
                        <td className="px-4 py-4">
                          <div>
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {pet.ownerName || 'N/A'}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {pet.ownerPhone || 'N/A'}
                            </p>
                          </div>
                        </td>

                        {/* Location */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <MapPin size={14} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                            <div>
                              <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {pet.uc || 'N/A'}
                              </p>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {pet.tahsil || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                            {pet.vaccinationStatus === VACCINATION_STATUS.VACCINATED && <ShieldCheck size={12} />}
                            {pet.vaccinationStatus === VACCINATION_STATUS.FIRST_SHOT && <Syringe size={12} />}
                            {pet.vaccinationStatus === VACCINATION_STATUS.FIRST_EXPIRED && <AlertTriangle size={12} />}
                            {pet.vaccinationStatus === VACCINATION_STATUS.VACCINATION_EXPIRED && <AlertTriangle size={12} />}
                            {statusBadge.label}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${darkMode
                              ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70'
                              : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                              }`}
                            onClick={() => {
                              handleModelOpen(pet);
                            }}
                          >
                            <Eye size={14} />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination - works for both views */}
        {!loading && filteredPets.length > 0 && (
          <div className={`mt-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-4`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{indexOfFirstItem + 1}</span> to{' '}
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Math.min(indexOfLastItem, filteredPets.length)}</span> of{' '}
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{filteredPets.length}</span> records
              </div>

              <div className="flex items-center gap-2">
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`px-2 py-1.5 rounded-lg border text-sm ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage === 1
                      ? 'opacity-50 cursor-not-allowed'
                      : darkMode
                        ? 'hover:bg-gray-700 text-gray-400'
                        : 'hover:bg-gray-100 text-gray-600'
                      }`}
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <span className={`px-3 py-1.5 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {currentPage} / {totalPages || 1}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className={`p-2 rounded-lg transition-all ${currentPage === totalPages || totalPages === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : darkMode
                        ? 'hover:bg-gray-700 text-gray-400'
                        : 'hover:bg-gray-100 text-gray-600'
                      }`}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {
        isModalOpen && (
          <PetDetailsModal
            pet={selectedPet}
            onClose={() => setIsModalOpen(false)}
            darkMode={darkMode}
          />
        )
      }
    </div>
  );
};

export default PetRegisteredView;

const PetDetailsModal = ({ pet, onClose, darkMode = false }) => {
  const [showFullImage, setShowFullImage] = useState(false);

  // Get status badge styling - matching website theme
  const getStatusBadge = (vaccinationStatus) => {
    switch (vaccinationStatus) {
      case VACCINATION_STATUS.VACCINATED:
        return {
          bg: darkMode ? 'bg-emerald-900/50' : 'bg-emerald-100',
          text: darkMode ? 'text-emerald-300' : 'text-emerald-700',
          label: 'Vaccinated',
          icon: ShieldCheck
        };
      case VACCINATION_STATUS.FIRST_SHOT:
        return {
          bg: darkMode ? 'bg-cyan-900/50' : 'bg-cyan-100',
          text: darkMode ? 'text-cyan-300' : 'text-cyan-700',
          label: 'First Shot',
          icon: Syringe
        };
      case VACCINATION_STATUS.FIRST_EXPIRED:
        return {
          bg: darkMode ? 'bg-red-900/50' : 'bg-red-100',
          text: darkMode ? 'text-red-300' : 'text-red-700',
          label: 'First Shot Expired',
          icon: AlertTriangle
        };
      case VACCINATION_STATUS.VACCINATION_EXPIRED:
        return {
          bg: darkMode ? 'bg-red-900/50' : 'bg-red-100',
          text: darkMode ? 'text-red-300' : 'text-red-700',
          label: 'Vaccination Expired',
          icon: AlertTriangle
        };
      default:
        return {
          bg: darkMode ? 'bg-purple-900/50' : 'bg-purple-100',
          text: darkMode ? 'text-purple-300' : 'text-purple-700',
          label: 'Registered',
          icon: ClipboardList
        };
    }
  };

  const statusBadge = getStatusBadge(pet?.vaccinationStatus);
  const StatusIcon = statusBadge.icon;

  if (!pet) return null;

  // Info item component matching website theme
  const InfoItem = ({ icon: Icon, label, value, iconBg, iconColor }) => (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${iconBg || (darkMode ? 'bg-gray-700' : 'bg-gray-100')}`}>
        <Icon size={14} className={iconColor || (darkMode ? 'text-gray-400' : 'text-gray-500')} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {label}
        </p>
        <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {value || 'N/A'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div
          className={`relative w-full max-w-4xl rounded-xl shadow-lg transform transition-all ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 sm:p-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {/* Small Avatar */}
              <div className="flex-shrink-0">
                {pet.imageUrl ? (
                  <img
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover ring-2 ring-blue-500/20"
                    src={pet.imageUrl}
                    alt={pet.petName}
                  />
                ) : (
                  <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <Dog className={darkMode ? 'text-blue-400' : 'text-blue-600'} size={24} />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className={`text-lg sm:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {pet.petName || 'Unknown Pet'}
                  </h2>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon size={12} />
                    {statusBadge.label}
                  </span>
                </div>
                <p className={`text-xs sm:text-sm font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  ID: {pet.registrationId || 'N/A'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`flex-shrink-0 p-2 rounded-lg transition-all ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-5 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Left Side - Image Card */}
              <div className="w-full lg:w-2/5">
                <div
                  className={`rounded-xl overflow-hidden cursor-pointer group relative ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                  onClick={() => pet.imageUrl && setShowFullImage(true)}
                >
                  {pet.imageUrl ? (
                    <>
                      <img
                        className="w-full h-48 sm:h-64 lg:h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                        src={pet.imageUrl}
                        alt={pet.petName}
                      />
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <Eye size={16} className="text-gray-700" />
                          <span className="text-sm font-medium text-gray-700">View Full</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-48 sm:h-64 lg:h-80 flex items-center justify-center">
                      <div className="text-center">
                        <Dog size={48} className={darkMode ? 'text-gray-600 mx-auto mb-2' : 'text-gray-400 mx-auto mb-2'} />
                        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No Photo</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - 4 Info Cards */}
              <div className="w-full lg:w-3/5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {/* Pet Details Card */}
                  <div className={`p-4 rounded-xl shadow-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                        <Dog size={16} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                      </div>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Pet Details</h3>
                    </div>
                    <div className="space-y-2.5">
                      <InfoItem icon={Dog} label="Name" value={pet.petName} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                      <InfoItem icon={ClipboardList} label="Breed" value={pet.breed} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={Calendar} label="Age" value={pet.age ? `${pet.age}` : null} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={Eye} label="Color" value={pet.bodyCoatColour} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={User} label="Gender" value={pet.sex} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                    </div>
                  </div>

                  {/* Owner Card */}
                  <div className={`p-4 rounded-xl shadow-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                        <User size={16} className={darkMode ? 'text-purple-400' : 'text-purple-600'} />
                      </div>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Owner Info</h3>
                    </div>
                    <div className="space-y-2.5">
                      <InfoItem icon={User} label="Name" value={pet.ownerName} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-purple-400' : 'text-purple-600'} />
                      <InfoItem icon={Phone} label="Phone" value={pet.ownerPhone} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={CreditCard} label="CNIC" value={pet.ownerCNIC} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={Home} label="Address" value={pet.ownerAddress} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                    </div>
                  </div>

                  {/* Location Card */}
                  <div className={`p-4 rounded-xl shadow-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                        <MapPin size={16} className={darkMode ? 'text-emerald-400' : 'text-emerald-600'} />
                      </div>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Location</h3>
                    </div>
                    <div className="space-y-2.5">
                      <InfoItem icon={MapPin} label="District" value={pet.district} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-emerald-400' : 'text-emerald-600'} />
                      <InfoItem icon={MapPin} label="Tehsil" value={pet.tahsil} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                      <InfoItem icon={MapPin} label="Union Council" value={pet.uc} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                    </div>
                  </div>

                  {/* Vaccination Card */}
                  <div className={`p-4 rounded-xl shadow-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                        <Syringe size={16} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                      </div>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Vaccination</h3>
                    </div>
                    <div className="space-y-2.5">
                      {/* Status Badge */}
                      <div className={`p-2.5 rounded-lg ${statusBadge.bg}`}>
                        <div className="flex items-center gap-2">
                          <StatusIcon size={16} className={statusBadge.text} />
                          <span className={`text-sm font-semibold ${statusBadge.text}`}>{statusBadge.label}</span>
                        </div>
                      </div>
                      <InfoItem icon={Home} label="Hospital" value={pet.hospitalType} iconBg={darkMode ? 'bg-gray-600' : 'bg-white'} iconColor={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                      <InfoItem
                        icon={Calendar}
                        label="Registered"
                        value={pet.date ? new Date(pet.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null}
                        iconBg={darkMode ? 'bg-gray-600' : 'bg-white'}
                        iconColor={darkMode ? 'text-gray-400' : 'text-gray-600'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-end gap-3 px-4 sm:px-5 py-3 sm:py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={onClose}
              className={`px-4 sm:px-5 py-2 rounded-lg text-sm font-medium transition-all ${darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Full Screen Image Viewer */}
      {showFullImage && pet.imageUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowFullImage(false);
          }}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFullImage(false);
            }}
            className="absolute top-4 right-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
          >
            <X size={20} />
          </button>

          {/* Pet Name Badge */}
          <div className="absolute top-4 left-4 z-10">
            <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-lg">
              <p className="text-white font-semibold text-sm sm:text-base">{pet.petName}</p>
              <p className="text-white/70 text-xs">{pet.registrationId}</p>
            </div>
          </div>

          {/* Instructions */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
            <p className="text-white/50 text-xs sm:text-sm">Tap anywhere to close</p>
          </div>

          {/* Full Size Image */}
          <img
            src={pet.imageUrl}
            alt={pet.petName}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
